import { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import type { Student } from '../../types';
import { useGetStudentRegistrationResponseQuery } from '../../store/api/localApi';
import { RegistrationSnapshot } from './RegistrationSnapshot';

interface Props {
  student: Student;
}

/**
 * Registration block on the StudentDetail page. PENDING shows a quiet
 * placeholder (the existing ParentOnboardingCard handles "Resend invite" /
 * "Start over" admin recovery actions, which also wipes the form artefacts
 * if the admin needs a do-over). COMPLETE shows submission metadata and a
 * toggle for the read-only snapshot.
 */
export function RegistrationSection({ student }: Props) {
  const [expanded, setExpanded] = useState(false);
  const isComplete = student.registrationStatus === 'COMPLETE';
  const { data, error } = useGetStudentRegistrationResponseQuery(student.id, {
    skip: !isComplete,
  });

  // A 404 here is not a failure: the student is marked COMPLETE but the
  // submitted response is missing (e.g. an admin "start over" wiped the form
  // artefacts, or legacy/seed data). Treat it as "no snapshot available"
  // rather than a scary error. Any other status IS a real load failure.
  const status = (error as { status?: number } | undefined)?.status;
  const responseMissing = status === 404;
  const isError = !!error && !responseMissing;

  if (!isComplete) {
    return (
      <section
        data-testid="registration-section-pending"
        className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500"
      >
        Registration not yet submitted by the parent.
      </section>
    );
  }

  const response = data?.response;
  const submittedAt = response?.submittedAt ? formatDublinDateTime(response.submittedAt) : '—';
  const submittedBy = response?.submittedByName ?? 'Parent';

  return (
    <section
      data-testid="registration-section"
      className="rounded-lg border border-gray-200 bg-white"
    >
      <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <h3 className="font-medium text-gray-900">Registration</h3>
          {isError ? (
            <p className="text-sm text-red-600">Could not load registration details.</p>
          ) : responseMissing ? (
            <p className="text-sm text-gray-500">
              Registration complete. The submitted form is no longer on file.
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              Submitted by {submittedBy} on {submittedAt}
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="registration-section-toggle"
          onClick={() => setExpanded((v) => !v)}
          disabled={!response}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {expanded ? 'Hide details' : 'View registration form'}
        </button>
      </header>
      {expanded && response && <RegistrationSnapshot response={response} />}
    </section>
  );
}
